import React, { useState, useEffect } from "react";
import { getRequest, delRequest } from "../../axiosRoutes/index";
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  TableFooter,
  Pagination,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
} from "@mui/material";
import SideNavBar from "../sidebar/SideNav";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbar } from "../../axiosRoutes/snackbarConfig";
import AddRole from "./AddRole";

const Roles = () => {
  const { snackbar, showSnackbar, handleSnackbarClose } = useSnackbar();
  const [hover, setHover] = useState(null);
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const [roles, setRoles] = useState([]);
  const [openRole, setOpenRole] = useState(false);
  const [roleId, setRoleId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [updateId, setUpdateId] = useState(null);
  const [roleData, setRoleData] = useState({
    name: "",
    permissions: [],
  });
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const fetchRoles = async () => {
    const access = localStorage.getItem("access");
    try {
      const response = await getRequest("admin/role", {
        headers: {
          Authorization: `Bearer ${access}`,
        },
      });
      const capitalizeWords = (str) =>
        str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
      const formattedResponse = response.map((role) => ({
        ...role,
        name: capitalizeWords(role.name),
        permissions: role.permissions.map((permission) =>
          capitalizeWords(permission.replace(/([A-Z])/g, " $1").trim())
        ),
      }));

      setRoles(formattedResponse);
    } catch (error) {
      console.error("Error Fetching Roles:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleDelete = async () => {
    try {
      const access = localStorage.getItem("access");
      const response = await delRequest(`admin/role/${deleteId}`, access);
      fetchRoles();
      handleClose();
      showSnackbar(response, "success");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      showSnackbar(errorMessage, "error");
    }
  };

  const handleOpen = () => {
    setOpenModal(true);
  };

  const handleClickOpen = (id) => {
    setDeleteId(id);
    setOpen(true);
  };

  const selectRole = (id) => {
    setUpdateId(id);
    handleOpen();
  };

  const handleRole = (id) => {
    setOpenRole(true);
    setRoleId(id);
  };

  useEffect(() => {
    const fetchRole = async () => {
      if (roleId !== null) {
        const access = localStorage.getItem("access");
        try {
          setLoading(true);
          const res = await getRequest(`admin/role/${roleId}`, {
            headers: {
              Authorization: `Bearer ${access}`,
            },
          });
          const capitalizeWords = (str) =>
            str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
          setRoleData({
            name: capitalizeWords(res.name) || "N/A",
            permissions: res.permissions.map((permission) =>
              capitalizeWords(permission.replace(/([A-Z])/g, " $1").trim())
            ) || "N/A",
          });
        } catch (error) {
          console.error("Error fetching Role:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setRoleData({
          name: "",
          permissions: [],
        });
      }
    };
    fetchRole();
  }, [roleId]);

  const handleClose = () => {
    setOpenModal(false);
    setOpen(false);
    setOpenRole(false);
    fetchRoles();
    setDeleteId(null);
    setRoleId(null);
    setUpdateId(null);
  };

  const headingTableCellStyle = {
    color: "#fff",
    textAlign: "center",
    borderBottom: "2px solid #1F1F1F",
    fontSize: { xs: "0.8rem", sm: "1rem" },
    backgroundColor: "#1F1F1F",
  };

  const dataTableCellStyle = (hover, id) => {
    return {
      color: hover === id ? "#FF5722" : "#fff",
      textAlign: "center",
      borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
      fontSize: { xs: "0.8rem", sm: "1rem" },
      padding: "10px 16px",
      backgroundColor: "#292929",
    };
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        textAlign="center"
      >
        <CircularProgress size={50} sx={{ color: "#FF5722" }} />
        <Typography variant="h6" sx={{ marginTop: "18px", color: "#fff" }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <div className="app-container" style={{ color: "#fff", display: "flex" }}>
        <SideNavBar />
        <div style={{ flex: 1, margin: `${isSmallScreen ? '3.5rem' : '0.5rem'} 0.5rem 0.5rem 0.5rem` }}>
          <Box sx={{ backgroundColor: "#000", marginTop: { xs: 1, md: 0 } }}>
            <Grid
              container
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                padding: "1rem",
              }}
            >
              <Grid item>
                <h1>Roles</h1>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={handleOpen}
                  sx={{
                    backgroundColor: "#fff",
                    color: "black",
                    "&:hover": {
                      backgroundColor: "#e1e1e1",
                    },
                  }}
                >
                  Add Role
                </Button>
              </Grid>
            </Grid>
            {roles.length === 0 ? (
              <Typography variant="h6" sx={{ color: "#fff", textAlign: "center" }}>
                No Data Yet
              </Typography>
            ) : (
              <>
                {!isSmallScreen ? (
                  <TableContainer sx={{ overflowX: "auto", maxWidth: "100vw" }}>
                    <Table style={{
                        borderRadius: "25px",
                        borderCollapse: "separate",
                        overflow: "hidden",
                      }}>
                      <TableHead>
                        <TableRow>
                          <TableCell style={headingTableCellStyle}>
                            ID
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Role Title
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Permissions
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roles.map((role, id) => (
                          <TableRow key={id}
                          style={{
                            cursor: "pointer",
                          }}
                            onMouseEnter={() => setHover(id)}
                            onMouseLeave={() => setHover(null)}
                            onClick={() => handleRole(role._id)}
                          >
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              {id + 1}
                            </TableCell>
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              {role.name}
                            </TableCell>
                            <TableCell
                              style={{
                                color: hover === id ? "#FF5722" : "#fff",
                                textAlign: "center",
                                borderBottom:
                                  "1px solid rgba(255, 255, 255, 0.2)",
                                fontSize: { xs: "0.8rem", sm: "1rem" },
                                padding: "10px 16px",
                                backgroundColor: "#292929",
                                maxWidth: "200px",
                              }}
                            >
                              {role.permissions.map((permission) => `${permission}, `)}
                            </TableCell>
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#1976D2",
                                  marginRight: "5px",
                                }}
                                aria-label="view"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRole(role._id)
                                }}
                              >
                                <VisibilityIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#4CAF50",
                                  marginRight: "5px",
                                }}
                                aria-label="edit"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  selectRole(role._id)
                                }}
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#F44336",
                                  marginRight: "5px",
                                }}
                                aria-label="delete"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClickOpen(role._id)
                                }}
                              >
                                <DeleteIcon fontSize="inherit" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter style={{ backgroundColor: "#1F1F1F" }}>
                        <TableRow>
                          <TableCell
                            colSpan={100}
                            style={{
                              color: "white",
                              border: "none",
                              textAlign: "center",
                            }}
                          >
                            <Pagination
                              count={totalPages}
                              variant="outlined"
                              color="primary"
                              page={page}
                              onChange={(e, value) => {
                                setPage(value);
                              }}
                              sx={{
                                display: "inline-block",
                                "& .MuiPaginationItem-root": {
                                  color: "#fff",
                                  borderColor: "#fff",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    backgroundColor: "#FF5722",
                                    color: "#fff",
                                    borderColor: "#FF5722",
                                  },
                                  "&.Mui-selected": {
                                    backgroundColor: "#FF5722",
                                    color: "#fff",
                                    borderColor: "#FF5722",
                                  },
                                },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                ) : (
                  <Grid container spacing={1}>
                    {roles.map((role, id) => (
                      <Grid item xs={12} key={id}>
                        <Card
                          sx={{
                            backgroundColor: "#101010",
                            color: "#fff",
                          }}
                          onMouseEnter={() => setHover(id)}
                          onMouseLeave={() => setHover(null)}
                          onClick={() => handleRole(role._id)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <CardContent>
                              <Typography variant="h6">
                                {role.name}
                              </Typography>
                            </CardContent>
                            <CardActions>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#1976D2",
                                  marginRight: "1px",
                                }}
                                aria-label="view"
                                size="medium"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRole(role._id)
                                }}
                              >
                                <VisibilityIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#4CAF50",
                                  marginRight: "1px",
                                }}
                                aria-label="edit"
                                size="medium"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  selectRole(role._id)
                                }}
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#F44336",
                                  marginRight: "1px",
                                }}
                                aria-label="delete"
                                size="medium"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClickOpen(role._id)
                                }}
                              >
                                <DeleteIcon fontSize="inherit" />
                              </IconButton>
                            </CardActions>
                          </Box>
                        </Card>
                      </Grid>

                    ))}
                  </Grid>
                )}
              </>
            )}
          </Box>

          <Dialog
            open={open}
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "#212121",
                color: "#fff",
                padding: "15px",
              },
              "& .MuiDialogTitle-root": {
                padding: "5px",
              },
              "& .MuiDialogContentText-root": {
                color: "#fff",
              },
              "& .MuiDialogContent-root": {
                padding: "5px",
              },
              "& .MuiDialogActions-root": {
                padding: "5px",
              },
            }}
          >
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are You Sure You Want to Delete this Role?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                sx={{
                  backgroundColor: "#A6A6A6",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#B8B8B8" },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                sx={{
                  backgroundColor: "#E5622D",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#D0521B" },
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={openRole}
            maxWidth
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "#212121",
                opacity: 0.9,
                color: "#fff",
                padding: "5px",
                maxWidth: { xs: "90%", md: "30%" },
                border: "2px solid #D0521B",
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "28px",
                fontWeight: "bold",
              }}
            >
              {roleData.name}
              <IconButton
                onClick={handleClose}
                sx={{
                  color: "#fff",
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent>
              <Grid container justifyContent="space-between" alignItems="center" sx={{ borderTop: "2px solid #D0521B", paddingTop: "1rem", }}>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ marginBottom: 2 }}>
                    <b>Role Title:</b> {roleData.name}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                <Typography variant="body1" sx={{ marginBottom: 2 }}>
                    <b>Role Permissions:</b> {roleData.permissions.map((permission) => `${permission}, `)}
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
          </Dialog>
          <AddRole open={openModal} handleClose={handleClose} roleId={updateId} />
        </div>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Roles;
